/* eslint-disable no-restricted-imports */
import React, {useState, useEffect, useCallback} from 'react'
import {UIGameUserStatesProvider} from '../contexts/UIGameUserStatesContext'
import {UIGameStateProvider} from '../contexts/UIGameStateContext'
import {GameState, PlayerColor} from '@colonist/game-states'
import {GameClientUserState, GameSettings, UserReportReasonEnum} from '@colonist/game-base-types'
import {UIGameManager} from '../../../../code/client/shared/scripts/ui_game/UIGameManager'
import {UIGameUserSettingsProvider} from '../contexts/UIGameUserSettingsContext'
import {UIUtilsEjsVariables} from '../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {UIGameReplayProvider} from '../contexts/UIGameReplayContext'
import {UIGameSettingsProvider} from '../contexts/UIGameSettingsContext'
import {UIGameMyColorProvider} from '../contexts/UIGameMyColorContext'
import {UIGameDetailsProvider} from '../contexts/UIGameDetailsContext'
import {UIGamePlayersProvider} from '../contexts/UIGamePlayersContext'
import {UIGameSocketProvider} from '../contexts/UIGameSocketContext'
import {UIGamePauseProvider} from './UIGamePause/UIGamePauseContext'
import {UIGameLayout} from './UIGameLayout/UIGameLayout'
import {PIXI} from '@colonist/pixi'
import {UIGameSpectatorsProvider} from './UIGameSpectators/UIGameSpectatorsContext'
import {GameStateEnums} from '../../../../code/shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIContextProvider} from '../contexts/UIContext'
import {SectionBgAdaptiveView} from 'code/client/shared/scripts/ui_canvas_engine/SectionBgViewAdaptive'
import {UIGameVoteProvider} from './UIGameVote/UIGameVoteContext'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const globalGameSettingsSetter = {setGameSettings: (gameSettings: GameSettings) => {}}
export function setGlobalGameSettings(gameSettings: GameSettings) {
    globalGameSettingsSetter.setGameSettings(gameSettings)
}

let globalResizeUIGame: undefined | VoidFunction
export function resizeUIGame() {
    if(globalResizeUIGame) globalResizeUIGame()
}

export interface UIGameOptions {
    isChatEnabled: boolean
    isAfterLiveGame?: boolean | undefined
    initialCanResign: boolean
}

export interface UIGameProps {
    gameManager: UIGameManager
    initialUserStates: GameClientUserState[]
    initialGameState: GameState
    options: UIGameOptions
    gameSettings: GameSettings
    isReplay: boolean
    setCanvasOptions: (options: Partial<PIXI.IApplicationOptions>) => void
}

export default function UIGame({
    gameManager,
    initialUserStates,
    initialGameState,
    options,
    gameSettings: defaultGameSettings,
    isReplay,
    setCanvasOptions,
}: UIGameProps) {
    const [gameSettings, setGameSettings] = useState(defaultGameSettings)
    const isChatDefaultMinimized = gameManager.gameController.isBotGame()

    const [canvasWidth, setCanvasWidth] = useState(0)
    const [canvasHeight, setCanvasHeight] = useState(0)
    const [gameFeedsWidth, setGameFeedsWidth] = useState(0)
    const [gameFeedsHeight, setGameFeedsHeight] = useState(0)
    const [bottomHeight, setBottomHeight] = useState(0)

    globalGameSettingsSetter.setGameSettings = setGameSettings

    const isLandscapeMode = !UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
    const orientation = UIUtilsEjsVariables.isUserOnMobileApp && !isLandscapeMode ? 'portrait' : 'landscape'

    const onClickLog = useCallback((logIndex: number) => {
        gameManager.replayView?.replayController.clickedGameLogWithIndex(logIndex)
    }, [gameManager])

    const onSendChat = (message: string) => {
        gameManager.socketGameSend.submitChat(message)
    }

    const resize = () => {
        const canvas = gameManager.canvasController
        const gameContainer = document.getElementById('ui-game') as HTMLDivElement | null
        const view = canvas.app.view
        setCanvasWidth(canvas.appW)
        setCanvasHeight(canvas.appH)
        setGameFeedsWidth(canvas.chatWidth)
        setGameFeedsHeight(canvas.feedsHeight)
        setBottomHeight(canvas.bottomViewHeight)
        // TODO remove this when everything migrated to React and use CSS
        if(gameContainer) {
            gameContainer.style.top = view.getBoundingClientRect().top + 'px'
            gameContainer.style.left = view.style.left
            gameContainer.style.width = canvas.appW + 'px'
            gameContainer.style.height = canvas.appH + 'px'
        }
        gameManager.replayView?.setPosition()
    }

    globalResizeUIGame = resize

    useEffect(() => {
        if(!UIUtilsEjsVariables.isUserOnMobileApp) {
            window.addEventListener('resize', resize)
        }
        resize()

        return () => {
            if(!UIUtilsEjsVariables.isUserOnMobileApp) {
                window.removeEventListener('resize', resize)
            }
        }
    }, [])

    const {isDiscord, isRanked, matchmakingMatchType} = gameManager.gameController.gameDetails

    const sendVote = useCallback((voteId: number, accepted: boolean) => {
        gameManager.socketGameSend.clickedVote(voteId, accepted)
    }, [gameManager])

    const sendReportPlayer = useCallback((reportReason: UserReportReasonEnum, color: PlayerColor) => {
        gameManager.socketGameSend.reportPlayer(reportReason, color)
    }, [gameManager])

    const sendToggleEmbargoAgainstPlayer = useCallback((color: PlayerColor) => {
        gameManager.socketGameSend.toggleEmbargoAgainstPlayer(color)
    }, [gameManager])

    const sendTogglePause = useCallback(() => {
        gameManager.socketGameSend.togglePause()
    }, [gameManager])

    const onSetChatVisible = useCallback((visible: boolean) => {
        gameManager.eventController.sendEvent(visible, UIGameEventEnums.ToggleChatFeed)
    }, [gameManager])

    const onSetLogVisible = useCallback((visible: boolean) => {
        gameManager.eventController.sendEvent(visible, UIGameEventEnums.ToggleLogFeed)
    }, [gameManager])

    const [isOpenSettings, setIsOpenSettings] = useState(false)
    const isUserOnDiscord = UIUtilsEjsVariables.isUserOnDiscord

    // TODO remove when mobile end screen is reactjs
    const [tempMobileIsEndScreenVisible, setTempMobileIsEndScreenVisible] = useState(false)

    useEffect(() => {
        if(tempMobileIsEndScreenVisible === true) {
            onSetChatVisible(false)
            onSetLogVisible(false)
        }
    }, [tempMobileIsEndScreenVisible])

    gameManager.eventController.registerUIGameMechanic({
        onEvent: (data: any, eventType: GameStateEnums.UIGameEventEnums) => {
            switch(eventType) {
                case UIGameEventEnums.ReplayModeStart:
                case UIGameEventEnums.PopupActive: updateBottomHeight(data); break
                case GameStateEnums.UIGameEventEnums.GameEndHideMap: setTempMobileIsEndScreenVisible(true); break
                case GameStateEnums.UIGameEventEnums.GameEndShowMap: setTempMobileIsEndScreenVisible(false); break
                default: break
            }
        },
    })

    // TODO remove when UIGameTradeCreator, UIGameReplayView and UIGamePopup are migrated to React
    const updateBottomHeight = (popup?: SectionBgAdaptiveView) => {
        const padding = 4
        const replayViewHeight = gameManager.gameLayout?.getReplayViewHeight() || 0
        if(popup) {
            const appH = gameManager.canvasController.appH
            const bottom = appH - popup.y + padding + replayViewHeight
            setBottomHeight(bottom)
        } else {
            const bottomViewHeight = gameManager.canvasController.bottomViewHeight
            const bottom = bottomViewHeight + padding + replayViewHeight
            setBottomHeight(bottom)
        }
    }

    useEffect(() => {
        updateBottomHeight()
    }, [])

    return (
        <UIContextProvider isUserOnDiscord={isUserOnDiscord} isUserOnMobile={UIUtilsEjsVariables.isUserOnMobile}>
            <UIGameMyColorProvider myColor={gameManager.gameController.myColor}>
                <UIGameDetailsProvider isDiscord={isDiscord} isRanked={isRanked} matchmakingMatchType={matchmakingMatchType}>
                    <UIGameSettingsProvider gameSettings={gameSettings}>
                        <UIGameUserStatesProvider initialUserStates={initialUserStates}>
                            <UIGamePlayersProvider playerStates={initialGameState.playerStates}>
                                <UIGameSocketProvider
                                    sendVote={sendVote}
                                    sendReportPlayer={sendReportPlayer}
                                    sendToggleEmbargoAgainstPlayer={sendToggleEmbargoAgainstPlayer}
                                    sendTogglePause={sendTogglePause}
                                    sendLeaveGame={() => gameManager.socketGameSend.leaveGame()}
                                    sendResignGame={() => gameManager.socketGameSend.resign()}
                                    // eslint-disable-next-line i18next/no-literal-string
                                    sendDisableKarma={() => gameManager.socketGameSend.submitChat('/disablekarma')}>
                                    <UIGameReplayProvider defaultIsReplay={isReplay}>
                                        <UIGameVoteProvider>
                                            <UIGamePauseProvider isReplay={isReplay}>
                                                <UIGameStateProvider initialGameState={initialGameState}>
                                                    <UIGameUserSettingsProvider
                                                        isOpenSettings={isOpenSettings}
                                                        setIsOpenSettings={setIsOpenSettings}
                                                        setCanvasOptions={setCanvasOptions}>
                                                        <UIGameSpectatorsProvider>
                                                            <UIGameLayout
                                                                orientation={orientation}
                                                                canvasWidth={canvasWidth}
                                                                canvasHeight={canvasHeight}
                                                                gameFeedsWidth={gameFeedsWidth}
                                                                gameFeedsHeight={gameFeedsHeight}
                                                                bottomHeight={bottomHeight}
                                                                itemSize={gameManager.canvasController.itemSize}
                                                                isChatDefaultMinimized={isChatDefaultMinimized}
                                                                isChatEnabled={options.isChatEnabled}
                                                                onSetChatVisible={onSetChatVisible}
                                                                onSetLogVisible={onSetLogVisible}
                                                                onClickLog={onClickLog}
                                                                onSendChat={onSendChat}
                                                                tempMobileIsEndScreenVisible={tempMobileIsEndScreenVisible}
                                                                initialCanResign={options.initialCanResign}/>
                                                        </UIGameSpectatorsProvider>
                                                    </UIGameUserSettingsProvider>
                                                </UIGameStateProvider>
                                            </UIGamePauseProvider>
                                        </UIGameVoteProvider>
                                    </UIGameReplayProvider>
                                </UIGameSocketProvider>
                            </UIGamePlayersProvider>
                        </UIGameUserStatesProvider>
                    </UIGameSettingsProvider>
                </UIGameDetailsProvider>
            </UIGameMyColorProvider>
        </UIContextProvider>
    )
}
